import React from "react";
import dayjs from "dayjs";
import { Grid, Button, Card, Tag, Input, Select } from "antd";
import { EyeOutlined, MessageOutlined, LinkOutlined, PhoneOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useDispatch, useSelector } from "react-redux";
import { updateDealStage, fetchDeals, updateDealMeta } from "../dealThunk";
import {updateDealLocal, updateStageLocal} from "../dealSlice";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { useState } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { convertToCompanyTimezoneObject } from "../../../../lib/functions";
import { resolveDialableNumber } from "../../../../lib/phone";

const stages = ["prospecting", "negotiation", "won", "lost"];

const DealKanban = ({ authInfo, deals, onViewActivity, setOpen }) => {

  const dispatch = useDispatch();

  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [openStage, setOpenStage] = useState(null);
  const [editingDealId, setEditingDealId] = useState(null);
  const [tempValue, setTempValue] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (isMobile && stages.length) {
      setOpenStage(stages[0]);
    }
  }, [isMobile]);

 

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    if (
      result.destination.droppableId === result.source.droppableId
    ) {
      return;
    }

    const dealId = result.draggableId;
    const newStage = result.destination.droppableId;

    // ✅ 1. instant UI update (NO REFETCH)
    dispatch(updateStageLocal({ deal_id: dealId, stage: newStage }));

    // ✅ 2. async backend update
    dispatch(updateDealStage({
      deal_id: dealId,
      stage: newStage
    }));
  };

  const isInactive = (deal) => {
    if (!deal.last_activity_at) return true;

    // return dayjs().diff(dayjs(deal.last_activity_at), "day") >= 3;
    return dayjs().diff(convertToCompanyTimezoneObject(deal.last_activity_at), "day") >= 3;
  };
  
  const stageColor = {
    prospecting: "orange",
    negotiation: "blue",
    won: "green",
    lost: "red",
  };  

  const getStageTotal = (stage) => {
    return deals
      .filter(d => d.stage === stage)
      .reduce((sum, d) => sum + Number(d.value || 0), 0);
  };

  const stageProbability = {
    prospecting: 0.1,
    negotiation: 0.5,
    won: 1,
    lost: 0,
  };

  const getWeightedTotal = (stage) => {
    return deals
      .filter(d => d.stage === stage)
      .reduce((sum, d) => {
        const prob = stageProbability[d.stage] || 0;
        return sum + Number(d.value || 0) * prob;
      }, 0);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
          style={
            isMobile
              ? { display: "block" }
              : {
                  display: "flex",
                  gap: 16,
                  overflowX: "auto",
                  paddingBottom: 10,
                }
          }
        >
        {stages.map((stage) => (
          <Droppable droppableId={stage} key={stage}>
            {(provided, snapshot) => (
              <div 
                ref={provided.innerRef} 
                {...provided.droppableProps} 
                style={{
                  width: isMobile ? "100%" : 280,
                  minHeight: 200,
                  marginBottom: isMobile ? 16 : 0,
                  flexShrink: 0,
                  background: snapshot.isDraggingOver ? "#e6f4ff" : "#fafafa",
                  padding: 8,
                  borderRadius: 8,
                }}>
                  <div style={{ marginBottom: 10 }}>
                  <div
                  style={{ marginBottom: 10, cursor: "pointer", textAlign: "center" }}
                  onClick={() =>
                    setOpenStage(openStage === stage ? null : stage)
                  }
                >
                  <Tag color={stageColor[stage]} style={{display: "flex", justifyContent: "center"}}>
                <h3>{stage.toUpperCase()}</h3>
                <span style={{ marginLeft: 8 }}>
                  ({deals.filter(d => d.stage === stage).length})
                </span>
                </Tag>
                <div style={{ fontSize: 12, marginTop: 4, color: "#555", textAlign:"center" }}>
                  STAGE TOTAL : {authInfo?.currency} {getStageTotal(stage).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  Weighted: {authInfo?.currency} {getWeightedTotal(stage).toLocaleString()}
                </div>
                </div>
                <Button
                  type="dashed"
                  size="small"
                  block
                  style={{ marginTop: 6 }}
                  onClick={() => {
                    setOpen(stage); // open create deal modal
                  }}
                >
                  + Add Deal
                </Button>
                </div>

                {(!isMobile || openStage === stage) &&
                deals
                  .filter((d) => d.stage === stage)
                  .map((deal, index) => (
                    <Draggable key={deal.deal_id} draggableId={String(deal.deal_id)} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          size="small"
                          style={{
                            marginBottom: 10,
                            border: isInactive(deal) ? "1px solid red" : "",
                            borderRadius: 12,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            background: snapshot.isDragging
                              ? "#687a9b"
                              : isInactive(deal)
                              ? "#fff1f0"
                              : "",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <b>{deal.title}</b>
                          <div style={{ fontSize: 12, color: "#888" }}>
                            {deal.contact_name}
                          </div>
                          <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {editingDealId === deal.deal_id ? (
                            <Input
                              size="small"
                              value={tempValue}
                              onChange={(e) => setTempValue(e.target.value)}
                              onPressEnter={async () => {
                                 dispatch(updateDealLocal({
                                  deal_id: deal.deal_id,
                                  value: tempValue
                                }));

                                dispatch(updateDealMeta({
                                  deal_id: deal.deal_id,
                                  value: tempValue
                                }));

                                dispatch(fetchDeals());
                                setEditingDealId(null);
                              }}
                              onBlur={() => setEditingDealId(null)}
                            />
                          ) : (
                          <Tag color="green" style={{ margin: 0 }} onDoubleClick={() => {
                              setEditingDealId(deal.deal_id);
                              setTempValue(deal.value);
                            }}>
                            {authInfo?.currency} {Number(deal.value || 0).toLocaleString()}
                          </Tag>
                          )}
                          <Select
                            size="small"
                            style={{ flex: "1 1 100%", width: "100%", fontSize: 13 }}
                            value={deal.stage}
                            onChange={(val) => {
                              dispatch(updateStageLocal({ deal_id: deal.deal_id, stage: val }));
                              dispatch(updateDealStage({ deal_id: deal.deal_id, stage: val }));
                            }}
                            options={stages.map(s => ({
                              label: s.charAt(0).toUpperCase() + s.slice(1),
                              value: s
                            }))}
                          />
                          <Tag color="blue" style={{ margin: 0 }}>
                            Activity: {deal.activity_count || 0}
                          </Tag>
                          {isInactive(deal) && (
                            <Tag color="red" style={{ margin: 0 }}>No Activity</Tag>
                          )}
                          {/* Custom Fields in Card  */}
                          <div style={{ marginTop: 6 }}>
                            {Object.entries(deal.custom_fields || {})
                              .slice(0, 2)
                              .map(([k, v]) => (
                                <Tag key={k}>{v}</Tag>
                              ))}
                          </div>

                          </div>
                          
                          
                        <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "space-between", }}>
                          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/crm/deals/${deal.deal_id}`)}>
                            View
                          </Button>

                          <Button size="small" icon={<MessageOutlined />} onClick={() => onViewActivity(deal)}>
                            Activity
                          </Button>

                          <Button size="small" icon={<LinkOutlined />} onClick={() => navigate(`/crm/leads/${deal.contact_id}`)}>
                            Lead
                          </Button>

                          <Button size="small" icon={<PhoneOutlined />} onClick={() => window.open(`tel:${resolveDialableNumber(deal.phone, deal.country)}`)} >
                            Call
                          </Button>
                        </div>
                        </Card>
                      )}
                    </Draggable>
                  ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
};

export default DealKanban;